import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  OnDestroy,
  OnInit,
  effect,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { EzGridComponent } from '@template-ui/components/ez-grid/ez-grid.component';
import { ButtonsCellRenderer } from '@template-ui/components/ez-grid/cell-renderers/buttons-cell-renderer/buttons-cell-renderer.component';
import type { ColDef, GridOptions } from 'ag-grid-community';
import { RoleService } from '@app/shared/services/roles.service';
import { workflowTimerReasonCommands } from '@app/shared/workflow-commands/workflow-reason-catalog';
import {
  PlatformLookupMetadataAdminService,
  mapLookupMetadataAdminError,
} from '../lookup-metadata/platform-lookup-metadata-admin.service';
import type {
  LookupMetadataConfigurationVersion,
  LookupMetadataDomainDetail,
  LookupMetadataEntry,
  LookupMetadataEntryUpsertRequest,
} from '../lookup-metadata/platform-lookup-metadata-admin.models';
import { ReasonCatalogDraftStateService } from './reason-catalog-draft-state.service';

const emptyValues = () => ({
  code: '',
  displayText: '',
  isActive: true,
  sortOrder: 0,
});
@Component({
  selector: 'phms-platform-reason-catalogs',
  imports: [
    CommonModule,
    FormsModule,
    UiButtonDirective,
    UiControlDirective,
    EzGridComponent,
  ],
  templateUrl: './platform-reason-catalogs.component.html',
  styleUrl: './platform-reason-catalogs.component.scss',
})
export class PlatformReasonCatalogsComponent implements OnInit, OnDestroy {
  private readonly api = inject(PlatformLookupMetadataAdminService);
  private readonly roles = inject(RoleService);
  private readonly destroy = inject(DestroyRef);
  readonly draftState = inject(ReasonCatalogDraftStateService);
  readonly canRead = this.roles.hasAnyPermissionSignal(
    'platform_access_admin.read',
  );
  readonly canAuthor = this.roles.hasAnyPermissionSignal(
    'platform_access_admin.author',
  );
  readonly domains = Object.values(workflowTimerReasonCommands);
  domainCode = this.domains[0].domainCode as string;
  detail: LookupMetadataDomainDetail | null = null;
  versionId = '';
  selectedEntry: LookupMetadataEntry | null = null;
  editing = false;
  loading = false;
  stale = false;
  accessLost = false;
  error = '';
  notice = '';
  values = emptyValues();
  private generation = 0;
  private entriesSource: LookupMetadataEntry[] | null = null;
  private entriesVersion = '';
  private entriesCache: LookupMetadataEntry[] = [];
  private readonly accessEffect = effect(() => {
    if (!this.canRead()) this.clearProtected();
    else if (!this.canAuthor() && this.draftState.dirty()) this.reset();
  });
  readonly columns: ColDef[] = [
    { field: 'code', headerName: 'Code', minWidth: 150 },
    { field: 'displayText', headerName: 'Reason', flex: 1, minWidth: 180 },
    { field: 'configurationCode', headerName: 'Configuration', minWidth: 170 },
    { field: 'versionCode', headerName: 'Version', minWidth: 140 },
    { field: 'versionStatusCode', headerName: 'Status', minWidth: 110 },
    {
      field: 'isActive',
      headerName: 'Enabled',
      minWidth: 90,
      valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
    },
    { field: 'sortOrder', headerName: 'Order', minWidth: 85 },
    {
      headerName: '',
      minWidth: 100,
      cellRenderer: ButtonsCellRenderer,
      cellRendererParams: {
        buttonsConfig: [{ id: 'open', buttonText: 'Open' }],
        onButtonClick: ({ rowData }: { rowData: LookupMetadataEntry }) =>
          this.openEntry(rowData),
      },
    },
  ];
  readonly gridOptions: GridOptions = {
    getRowId: ({ data }) => String(data.id),
    onRowClicked: ({ data }) => {
      if (data) this.openEntry(data as LookupMetadataEntry);
    },
  };
  ngOnInit(): void {
    this.load();
  }
  ngOnDestroy(): void {
    ++this.generation;
    this.draftState.dirty.set(false);
    this.draftState.busy.set(false);
    this.accessEffect.destroy();
  }
  @HostListener('window:beforeunload', ['$event']) protectEdits(
    event: BeforeUnloadEvent,
  ): void {
    if (this.draftState.isNavigationBlocked()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }
  private clearProtected(): void {
    ++this.generation;
    this.detail = null;
    this.versionId = '';
    this.loading = false;
    this.draftState.busy.set(false);
    this.reset();
    this.notice = '';
  }
  versions(): LookupMetadataConfigurationVersion[] {
    return this.detail?.configurationVersions ?? [];
  }
  selectedVersion(): LookupMetadataConfigurationVersion | undefined {
    return this.versions().find(
      (version) => String(version.id) === this.versionId,
    );
  }
  entries(): LookupMetadataEntry[] {
    const source = this.detail?.entries ?? null;
    if (
      source !== this.entriesSource ||
      this.versionId !== this.entriesVersion
    ) {
      this.entriesSource = source;
      this.entriesVersion = this.versionId;
      this.entriesCache = source
        ? this.versionId
          ? source.filter(
              (entry) =>
                String(entry.configurationVersionId) === this.versionId,
            )
          : source
        : [];
    }
    return this.entriesCache;
  }
  versionLabel(version: LookupMetadataConfigurationVersion): string {
    return `${version.configurationCode} · ${version.displayName || version.versionCode}${version.displayName && version.displayName !== version.versionCode ? ' (' + version.versionCode + ')' : ''} · ${version.statusCode}`;
  }
  editable(): boolean {
    return (
      this.canRead() &&
      this.canAuthor() &&
      !this.accessLost &&
      !this.loading &&
      !this.stale &&
      this.selectedVersion()?.statusCode === 'draft'
    );
  }
  chooseDomain(domain: string): void {
    if (
      this.draftState.isNavigationBlocked() ||
      !this.domains.some((item) => item.domainCode === domain)
    )
      return;
    this.domainCode = domain;
    this.versionId = '';
    this.reset();
    this.load();
  }
  chooseVersion(id: string): void {
    if (
      this.draftState.isNavigationBlocked() ||
      (id && !this.versions().some((version) => String(version.id) === id))
    )
      return;
    this.versionId = id;
    this.reset();
  }
  load(): void {
    if (!this.canRead() || this.draftState.isNavigationBlocked()) return;
    const generation = ++this.generation,
      domain = this.domainCode;
    this.loading = true;
    this.detail = null;
    this.error = '';
    this.accessLost = false;
    this.reset();
    this.api
      .getDomain('lookup-values', domain)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: (detail) => {
          if (generation !== this.generation || !this.canRead()) return;
          this.loading = false;
          if (!this.validDetail(detail, domain)) {
            this.error =
              'The reason catalog could not be verified. Refresh before editing.';
            return;
          }
          this.detail = detail;
          if (this.versionId && !this.selectedVersion()) this.versionId = '';
        },
        error: (error: unknown) => {
          if (generation !== this.generation) return;
          this.loading = false;
          this.error = mapLookupMetadataAdminError(error).messages.join(' ');
          if (
            error instanceof HttpErrorResponse &&
            [401, 403].includes(error.status)
          ) {
            this.clearProtected();
            this.accessLost = true;
          }
        },
      });
  }
  private validDetail(
    detail: LookupMetadataDomainDetail,
    domain: string,
  ): boolean {
    return (
      !!detail &&
      detail.catalogTypeCode === 'lookup-values' &&
      detail.domainCode === domain &&
      Array.isArray(detail.entries) &&
      Array.isArray(detail.configurationVersions) &&
      new Set(detail.configurationVersions.map((version) => version.id))
        .size === detail.configurationVersions.length &&
      detail.configurationVersions.every(
        (version) =>
          Number.isSafeInteger(version.id) &&
          version.id > 0 &&
          !!version.configurationCode &&
          !!version.versionCode &&
          !!version.statusCode &&
          Number.isFinite(Date.parse(version.effectiveStartUtc)),
      ) &&
      detail.entries.every(
        (entry) =>
          entry.catalogTypeCode === 'lookup-values' &&
          entry.domainCode === domain &&
          Number.isSafeInteger(entry.id) &&
          entry.id > 0 &&
          !!entry.code &&
          typeof entry.displayText === 'string',
      )
    );
  }
  openEntry(entry: LookupMetadataEntry): void {
    if (
      this.loading ||
      this.draftState.isNavigationBlocked() ||
      !this.detail?.entries.includes(entry)
    )
      return;
    const version = this.versions().find(
      (item) =>
        item.id === entry.configurationVersionId &&
        item.configurationCode === entry.configurationCode &&
        item.versionCode === entry.versionCode &&
        item.statusCode === entry.versionStatusCode,
    );
    if (!version) {
      this.error = 'The entry version is unavailable. Refresh before editing.';
      return;
    }
    this.versionId = String(version.id);
    this.selectedEntry = entry;
    this.editing = true;
    this.stale = false;
    this.error = '';
    this.notice = '';
    this.values = {
      code: entry.code,
      displayText: entry.displayText,
      isActive: entry.isActive,
      sortOrder: entry.sortOrder,
    };
  }
  create(): void {
    if (!this.editable() || this.draftState.isNavigationBlocked()) return;
    this.reset();
    this.editing = true;
  }
  changed(): void {
    if (this.editable() && this.editing) this.draftState.dirty.set(true);
  }
  reset(): void {
    if (this.draftState.busy()) return;
    this.selectedEntry = null;
    this.editing = false;
    this.stale = false;
    this.values = emptyValues();
    this.draftState.dirty.set(false);
  }
  save(): void {
    const version = this.selectedVersion();
    if (!this.editing || !this.editable() || this.draftState.busy() || !version)
      return;
    const code = this.values.code.trim(),
      label = this.values.displayText.trim(),
      order = Number(this.values.sortOrder);
    if (
      !/^[a-z0-9][a-z0-9._:/-]{0,119}$/.test(code) ||
      !label ||
      label.length > 200 ||
      [...label].some((c) => c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127) ||
      !Number.isSafeInteger(order) ||
      order < 0 ||
      order > 2147483647
    ) {
      this.error =
        'Use a stable lower-case code, a reason label of up to 200 characters, and a nonnegative whole-number order.';
      return;
    }
    const entry = this.selectedEntry;
    const request: LookupMetadataEntryUpsertRequest = {
      catalogTypeCode: 'lookup-values',
      domainCode: this.domainCode,
      configurationVersionId: version.id,
      code,
      displayText: label,
      isActive: this.values.isActive,
      sortOrder: order,
      effectiveStartUtc: version.effectiveStartUtc,
      effectiveEndUtc: version.effectiveEndUtc ?? null,
      scopeTypeCode: 'global',
      scopeKey: null,
      metadata: { isDefault: entry?.metadata?.['isDefault'] ?? 'false' },
    };
    const generation = this.generation;
    this.draftState.busy.set(true);
    this.error = '';
    this.notice = '';
    const saving = entry
      ? this.api.updateEntry('lookup-values', entry.id, request)
      : this.api.createEntry(request);
    saving.pipe(takeUntilDestroyed(this.destroy)).subscribe({
      next: () => {
        if (generation !== this.generation) return;
        this.draftState.busy.set(false);
        this.reset();
        this.notice =
          'Draft reason saved. Publication and command configuration are managed separately.';
        this.load();
      },
      error: (error: unknown) => {
        if (generation !== this.generation) return;
        this.draftState.busy.set(false);
        this.error = mapLookupMetadataAdminError(error).messages.join(' ');
        if (
          error instanceof HttpErrorResponse &&
          [401, 403].includes(error.status)
        ) {
          this.clearProtected();
          this.accessLost = true;
        } else if (
          error instanceof HttpErrorResponse &&
          [409, 412, 428].includes(error.status)
        )
          this.stale = true;
      },
    });
  }
}
